import { useTranslation } from 'react-i18next';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui';
import { HIDDEN_BALANCES_FULL_TEXT } from '@/shared/config';
import { formatBalance } from '@/shared/lib';
import { cn } from '@/shared/utils';
import LockIcon from '@/shared/assets/lock-small-icon.svg?react';
import type { BalanceRow } from '../model';

type Props = {
  rows: BalanceRow[];
  basePrecission: number;
  baseCode: string;
  isBalancesHidden: boolean;
  isTabletUp: boolean;
};

const LABEL_KEYS: Record<BalanceRow['key'], string> = {
  total: 'personalAccount.balanceDropdown.balance.total',
  processing: 'personalAccount.balanceDropdown.balance.processing',
  inTrading: 'personalAccount.balanceDropdown.balance.inTrading',
  available: 'personalAccount.balanceDropdown.balance.available',
};

type RowProps = {
  row: BalanceRow;
  valueText: string;
  hint?: string;
  isTabletUp: boolean;
};

function Row({ row, valueText, hint, isTabletUp }: RowProps) {
  const { t } = useTranslation();

  const lock = row.locked ? (
    <LockIcon className="size-[1.2rem] text-tertiary shrink-0" />
  ) : null;

  const content = (
    <div
      className={cn(
        'flex items-center justify-between gap-[0.3rem] px-[1.2rem] h-[3rem] rounded-[0.8rem]',
        hint && 'hover:bg-secondary-bg transition-colors',
        hint && isTabletUp && 'cursor-help'
      )}>
      <span className="flex items-center gap-[0.3rem] p6 text-secondary min-w-0">
        {hint && !isTabletUp ? (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={t(LABEL_KEYS[row.key])}
                className="flex items-center cursor-pointer">
                {lock}
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              sideOffset={6}
              className="w-[24rem] rounded-[1.2rem] bg-background border-stroke p-[1.2rem] p6 text-primary shadow-dropdown!">
              {hint}
            </PopoverContent>
          </Popover>
        ) : (
          lock
        )}
        <span className="truncate">{t(LABEL_KEYS[row.key])}</span>
      </span>
      <span className="p6 text-primary whitespace-nowrap">{valueText}</span>
    </div>
  );

  if (hint && isTabletUp) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent
          side="left"
          sideOffset={16}
          className="max-w-[26rem] rounded-[1.2rem] px-[1.2rem] py-[0.8rem] p6 whitespace-normal">
          {hint}
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
}

export function BalanceSection({
  rows,
  basePrecission,
  baseCode,
  isBalancesHidden,
  isTabletUp,
}: Props) {
  const { t } = useTranslation();

  return (
    <section className="flex flex-col">
      <h3 className="medium5 text-primary px-[1.2rem]">
        {t('personalAccount.balanceDropdown.balance.title')}
      </h3>
      {rows.map(row => {
        const amount = isBalancesHidden
          ? HIDDEN_BALANCES_FULL_TEXT
          : formatBalance(row.value, basePrecission);

        return (
          <Row
            key={row.key}
            row={row}
            valueText={`${amount} ${baseCode}`}
            hint={row.hintKey ? t(row.hintKey) : undefined}
            isTabletUp={isTabletUp}
          />
        );
      })}
    </section>
  );
}
